/* eslint-disable prettier/prettier */
import { useCallback, useMemo } from 'react';
import { Box, ListItem, IconButton, ListItemText, Badge } from '@mui/material';
import { Participant, RemoteAudioTrack } from 'twilio-video';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

import { ChatIcon, MicOffIcon } from 'assets';
import {
  useChat,
  useIsTrackEnabled,
  useIsGuestCall,
  useParticipantDotMenu,
  usePublications,
  useRoomHost,
  useSignalrConnection,
  useTrack,
} from 'hooks';
import { ParticipantDotMenu } from 'components/ParticipantDotMenu';
import { IParticipantDotMenuContext, ISignalrConnectionContext, ParticipantDotMenuDialogType } from 'context';
import { ApiUser, ApiWaitingRoomLinkProfile } from 'types';
import { useAppSelector } from 'store';
import { selectUser } from 'store/user';
import { selectWaitingRoomProfile } from 'store/waitingRoom';
import { getPreferredFullName, parseIdentity, parseParticipantIdentity } from 'utils';

import styles from './ParticipantListItem.module.scss';

type ParticipantListItemProps = {
  participant: Participant;
  onAfterOpenChat?: () => void;
};

export function ParticipantListItem({ participant, onAfterOpenChat }: ParticipantListItemProps) {
  const { t } = useTranslation('providerRoom');
  const isGuestCall = useIsGuestCall();
  const { signalrConnectionId }: ISignalrConnectionContext = useSignalrConnection();
  const publications = usePublications(participant);
  const audioPublication = publications.find((p) => p.kind === 'audio');
  const audioTrack = useTrack(audioPublication) as RemoteAudioTrack | undefined;
  const isTrackEnabled = useIsTrackEnabled(audioTrack as RemoteAudioTrack);
  const { onDialogOpen }: IParticipantDotMenuContext = useParticipantDotMenu();
  const { identity: chatIdentity, conversationsUnreadMessagesData, openChat } = useChat();
  const user: ApiUser | null = useAppSelector(selectUser);
  const waitingRoomProfile: ApiWaitingRoomLinkProfile | null = useAppSelector(selectWaitingRoomProfile);
  const { isHost } = useRoomHost();

  const [id, identity] = useMemo(() => {
    return parseParticipantIdentity(participant);
  }, [participant]);

  const { clinicUser } = user || {};
  const { preferedDisplayName: clinicUserPreferredDisplayName } = clinicUser || {};
  const { preferedDisplayName: waitingRoomProfilePreferredDisplayName, providerId } = waitingRoomProfile || {};

  const showPreferredFullName = (isHost && id === clinicUser?.id) || (!isHost && id === providerId);

  const preferredFullName = useMemo(() => {
    const clinicUserFullName = getPreferredFullName(clinicUserPreferredDisplayName, null);
    const waitingRoomFullName = getPreferredFullName(waitingRoomProfilePreferredDisplayName, identity);

    return showPreferredFullName ? clinicUserFullName ?? waitingRoomFullName : identity;
  }, [isHost, id, waitingRoomProfilePreferredDisplayName, clinicUserPreferredDisplayName]);

  const showChat: boolean = useMemo(() => {
    return (isHost && id !== user?.clinicUser.id) || (!isHost && id === waitingRoomProfile?.providerId);
  }, [isHost, id, waitingRoomProfile, user]);

  const showThreeDots: boolean = useMemo(() => {
    if (!isHost) {
      return false;
    }

    return id !== user?.clinicUser.id;
  }, [isHost, id, user]);

  const unreadMessagesCount: number = useMemo(() => {
    const chatUniqueName: string = isHost
      ? `$chat-${chatIdentity}-${parseIdentity(participant.identity)[0]}`
      : `$chat-${waitingRoomProfile!.providerId}-${chatIdentity}`;
    return conversationsUnreadMessagesData.get(chatUniqueName)?.unreadMessagesCount ?? 0;
  }, [conversationsUnreadMessagesData, waitingRoomProfile, participant, chatIdentity]);

  const onOpenChat = useCallback((): void => {
    if (isHost) {
      openChat(chatIdentity, parseIdentity(participant.identity)[0], preferredFullName ?? undefined);
    } else {
      openChat(waitingRoomProfile!.providerId!, chatIdentity);
    }
    onAfterOpenChat?.();
  }, [chatIdentity, participant, preferredFullName, waitingRoomProfile, isHost, openChat, onAfterOpenChat]);

  const handleMuteClick = (): void => {
    if (isHost && id !== user?.clinicUser.id) {
      onDialogOpen(ParticipantDotMenuDialogType.MUTE, participant);
    }
  };

  const iconMutedColor = isTrackEnabled ? (isGuestCall ? '#393534' : 'var(--app-text-muted)') : 'var(--app-error)';
  const iconDefaultColor = isGuestCall ? '#393534' : 'var(--app-text-muted)';

  return (
    <ListItem
      secondaryAction={
        <Box
          className={classnames(styles.actions, {
            [styles.actionsWithChat]: showChat,
          })}
        >
          <IconButton edge='end' aria-label={t('aria.muteParticipant')} onClick={handleMuteClick}>
            <MicOffIcon sx={{ color: iconMutedColor, fontSize: '20px' }} />
          </IconButton>

          {showChat && (
            <IconButton edge='end' aria-label={t('aria.chat')} onClick={onOpenChat}>
              <>
                <Badge badgeContent={unreadMessagesCount} className={styles.chatIconBadge} color='primary'>
                  <ChatIcon className={styles.chatIcon} sx={{ color: iconDefaultColor }} />
                </Badge>
              </>
            </IconButton>
          )}

          {showThreeDots && (
            <ParticipantDotMenu participant={participant} toggleClassName={styles.dotMenu} withRemove={isHost} />
          )}
        </Box>
      }
    >
      <ListItemText primary={preferredFullName} className={styles.text} />
    </ListItem>
  );
}
